import { Alert, AlertDescription, AlertTitle } from '@creanizer/ui'
import { Progress } from '@creanizer/ui'
import { AlertTriangle, CheckCircle, Info } from 'lucide-react'

interface WorldConsistencyCheckProps {
  consistency: {
    score: number
    issues: string[]
    suggestions: string[]
  }
}

export function WorldConsistencyCheck({ consistency }: WorldConsistencyCheckProps) {
  const getScoreColor = (score: number) => {
    if (score >= 80) return 'text-green-600'
    if (score >= 60) return 'text-yellow-600'
    return 'text-red-600'
  }
  
  const getScoreIcon = (score: number) => {
    if (score >= 80) return <CheckCircle className="h-4 w-4" />
    if (score >= 60) return <Info className="h-4 w-4" />
    return <AlertTriangle className="h-4 w-4" />
  }
  
  const getScoreMessage = (score: number) => {
    if (score >= 90) return '非常に優れた整合性です'
    if (score >= 80) return '良好な整合性です'
    if (score >= 70) return '概ね整合性が取れています'
    if (score >= 60) return '一部整合性に問題があります'
    if (score >= 50) return '整合性の改善が必要です'
    return '大幅な見直しが必要です'
  }
  
  return (
    <div className="card">
      <div className="p-6 space-y-4">
        <div>
          <h3 className="text-lg font-semibold mb-4">整合性チェック</h3>
          
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                {getScoreIcon(consistency.score)}
                <span className={`font-medium ${getScoreColor(consistency.score)}`}>
                  整合性スコア: {consistency.score}/100
                </span>
              </div>
              <span className="text-sm text-muted-foreground">
                {getScoreMessage(consistency.score)}
              </span>
            </div>
            
            <Progress value={consistency.score} className="h-2" />
          </div>
        </div>
        
        {consistency.issues && consistency.issues.length > 0 && (
          <Alert variant="destructive">
            <AlertTriangle className="h-4 w-4" />
            <AlertTitle>整合性の問題点</AlertTitle>
            <AlertDescription>
              <ul className="list-disc list-inside mt-2 space-y-1">
                {consistency.issues.map((issue, index) => (
                  <li key={index} className="text-sm">{issue}</li>
                ))}
              </ul>
            </AlertDescription>
          </Alert>
        )}
        
        {consistency.suggestions && consistency.suggestions.length > 0 && (
          <Alert>
            <Info className="h-4 w-4" />
            <AlertTitle>改善提案</AlertTitle>
            <AlertDescription>
              <ul className="list-disc list-inside mt-2 space-y-1">
                {consistency.suggestions.map((suggestion, index) => (
                  <li key={index} className="text-sm">{suggestion}</li>
                ))}
              </ul>
            </AlertDescription>
          </Alert>
        )}
        
        {consistency.score >= 80 && (!consistency.issues || consistency.issues.length === 0) && (
          <Alert className="border-green-200 bg-green-50">
            <CheckCircle className="h-4 w-4 text-green-600" />
            <AlertTitle className="text-green-800">
              優れた世界観
            </AlertTitle>
            <AlertDescription className="text-green-700">
              この世界観は内部的な一貫性が高く、創作活動の基盤として適しています。
            </AlertDescription>
          </Alert>
        )}
      </div>
    </div>
  )
}